/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/// Use Snowflake Composables
/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
///
/// PARITY: `scripts/dev/snowflake-runner.ts` mirrors the REST call this file
/// makes (same URL, headers, params, body). If you change the request shape
/// here — adding a header, tweaking a URL param, changing the body
/// envelope — update the runner so dev SQL testing stays in sync with what
/// the app actually sends.
import type { UseFetchOptions } from '#app'
import { v4 as uuidv4 } from 'uuid'
import { snowflakeHostFromSfId } from '~/stores/snowflakeAuth'
import { snowflakeParseResponse } from '~/scripts/snowflake/snowflakeResponseParser'
import type { SnowflakeAuthentication, SnowflakeOptions, SnowflakeResponse } from '~/types/typesSnowflake'

/// 💬 Bind variables for the SQL API v2 (`POST /api/v2/statements`).
/// Keys are 1-based placeholder positions as strings ("1", "2", …)
/// matching `?` placeholders in the statement text. Bind VALUES do not
/// appear in the consumer's QUERY_HISTORY text — use them for anything
/// sensitive (secrets, tokens) instead of inline literals. Purely
/// additive: when absent, requests are byte-identical to before.
export interface SnowflakeBindValue { type: string; value: string }
export type SnowflakeBindings = Record<string, SnowflakeBindValue>

async function snowflakeQueryExecution({ query, queryId, returnType = 'json', async, polling, executor = 'user', customConnection, transaction = 'auto', bindings }: SnowflakeOptions & { transaction?: boolean | 'auto'; bindings?: SnowflakeBindings }, fetchOptions?: UseFetchOptions<any>) {
  try {
    // Get Auth Token and Account Credentials
    //
    // 💬 `snowflakeAuth` returns the right Bearer for the user's auth
    // mode — OAuth refresh, PAT decryption, or a freshly-signed
    // KEYPAIR_JWT — plus a `tokenType` discriminator we use below
    // when setting Snowflake's auth-token-type header.
    const { snowflakeAuth } = useSnowflakeAuthStore()
    const { accountNamespace } = storeToRefs(useGlobalsStore())

    // Swap Snowflake Snippets
    query = query?.replace('{db}', accountNamespace.value || '')

    // 💬 Auto-wrap multi-statement queries in BEGIN/COMMIT.
    //
    // Snowflake's `/api/v2/statements` endpoint runs each REST call in its
    // own session. If we send `stmt1; stmt2; stmt3;` and stmt2 fails, stmt1
    // has already committed and stmt3 never runs — leaving the user with
    // partial state. Wrapping with `BEGIN; ...; COMMIT;` means: on any
    // statement failure, the rest skip, COMMIT never executes, and Snowflake
    // auto-rolls-back the open transaction when the per-call session closes.
    //
    // Caveats:
    //   - Snowflake DDL (CREATE/ALTER/DROP/...) implicitly commits, so DDL
    //     mid-transaction can't actually be rolled back. The wrapper is a
    //     no-op for those statements; it still helps any DML around them.
    //   - If the user's SQL already contains explicit BEGIN/COMMIT/ROLLBACK,
    //     we skip wrapping — they're managing their own boundaries.
    //   - Pass `transaction: false` to opt out, or `transaction: true` to
    //     force-wrap a single statement (rarely useful).
    const wrappedInTransaction = !!query && shouldWrapInTransaction(query, transaction)
    if (wrappedInTransaction && query) {
      query = `BEGIN;\n${query.trim().replace(/;\s*$/, '')};\nCOMMIT;`
    }

    const snowflakeCredentials = await snowflakeAuth(executor, customConnection)
    if (!snowflakeCredentials.accessToken || !snowflakeCredentials.sfId || !snowflakeCredentials.authType) throw new Error('Missing Snowflake Access Token, SFID, or AuthType')
    const fetch = snowflakeFetch({ query, queryId, executor, bindings }, snowflakeCredentials, fetchOptions)
    const url = queryId ? `/api/v2/statements/${queryId}` : '/api/v2/statements'
    const response: any = await fetch(url)

    // If Multiple Statements Were Sent - Get Results From the user's last
    // data-bearing statement. When we transaction-wrapped, statementHandles
    // is [BEGIN, ...user stmts, COMMIT] — the user's last is at length-2.
    // Otherwise, an unwrapped multi-statement returns the actual last handle
    // (preserves the prior behavior).
    if (response.statementHandles && response.statementHandles.length > 1) {
      const targetIndex = wrappedInTransaction
        ? response.statementHandles.length - 2
        : response.statementHandles.length - 1
      const lastQueryId: string = response.statementHandles[targetIndex]
      return await snowflakeQueryExecution({ queryId: lastQueryId, returnType, async, polling, executor, customConnection }, fetchOptions)
    }

    // Parse Response. Snowflake can also surface errors in the response body
    // (vs. as an HTTP error caught below) — typically for permission denials
    // and "object does not exist" cases. Humanize those before returning so
    // every caller (SqlEditor, dashboards) shows a friendly message.
    const parsed = snowflakeParseResponse(response, returnType)
    if (parsed && (parsed as any).status === 'ERROR') {
      const rawError = (parsed as any).error
      return { ...(parsed as any), error: humanizeSnowflakeError(rawError) } as SnowflakeResponse
    }
    return parsed
  } catch (error: any) {
    // Errors thrown by `onResponseError` arrive here. Log the raw message for
    // debugging, but return the user-friendly translation so the UI surface
    // doesn't expose Snowflake's terse "SQL access control error: …" prose.
    const errorMessage = error.message ? error.message : error
    console.error(`Snowflake Error: ${errorMessage}`)
    return { status: 'ERROR', error: humanizeSnowflakeError(errorMessage) } as SnowflakeResponse
  }
}

// //////////////////////////////////////////////////////////////////
// 💬 Error humanization
// //////////////////////////////////////////////////////////////////
/// Translate Snowflake's terse error strings into actionable messages aimed
/// at end users. The Snowflake message is preserved in `console.error` for
/// debugging, but the return value of `useSnowflake` carries the friendly
/// version. Add new patterns here as we encounter them.
export function humanizeSnowflakeError(rawMessage: unknown): string {
  const message = typeof rawMessage === 'string' ? rawMessage : String(rawMessage ?? '')
  if (!message) return 'Snowflake returned an unknown error.'

  // "Insufficient privileges to operate on <type> '<name>'"
  // (sometimes prefixed with "SQL access control error:")
  const insufficientMatch = message.match(/Insufficient privileges to operate on (\w+)\s+'?([^']+?)'?(?:\.|$)/i)
  if (insufficientMatch) {
    const objectType = insufficientMatch[1].toLowerCase()
    const objectName = insufficientMatch[2]
    return `Your current role does not have access to ${objectType} '${objectName}'. Try switching to a role with more permissions, if available.`
  }

  // Generic catch-all for other "insufficient privileges" phrasings.
  if (/insufficient privileges/i.test(message)) {
    return 'Your current role does not have permission to execute this query. Try switching to a role with more permissions, if available.'
  }

  // "Object 'X' does not exist or not authorized" — Snowflake intentionally
  // collapses "doesn't exist" and "not authorized" into one message so it
  // doesn't leak object existence to unprivileged users. We mirror the same
  // ambiguity but lean on the role-switch hint.
  const notAuthorizedMatch = message.match(/(?:Object|Table|View|Schema|Database|Warehouse|Role|Function)\s+['"]?([^'"\s]+)['"]?\s+does not exist or not authorized/i)
  if (notAuthorizedMatch) {
    return `'${notAuthorizedMatch[1]}' either doesn't exist or your current role can't see it. Try switching to a role with more permissions, if available.`
  }

  // Fall through — return the original so we don't hide useful detail
  // (compilation errors, syntax errors, timeouts, etc.) behind a generic
  // message. Friendly translations are opt-in per pattern.
  return message
}

// //////////////////////////////////////////////////////////////////
// 💬 Transaction-wrapping helpers
// //////////////////////////////////////////////////////////////////

/**
 * Decide whether to auto-wrap a SQL string in BEGIN/COMMIT.
 *
 * Wrap if all of the following are true:
 *   - caller didn't pass `transaction: false`
 *   - the query has 2+ effective statements (semicolons that aren't inside
 *     strings or comments)
 *   - the query doesn't already manage transactions explicitly
 *
 * `transaction: true` forces a wrap regardless (useful for tests or when a
 * caller knows the query will become multi-statement after expansion).
 */
function shouldWrapInTransaction(sql: string, transaction: boolean | 'auto'): boolean {
  if (transaction === false) return false
  if (transaction === true) return true
  // 'auto'
  if (hasExplicitTransaction(sql)) return false
  return countEffectiveStatements(sql) > 1
}

/**
 * Count effective (non-empty) statements separated by `;`. Splits after
 * stripping comments and string literals so semicolons inside strings or
 * comments don't inflate the count.
 */
function countEffectiveStatements(sql: string): number {
  return stripStringsAndComments(sql)
    .split(';')
    .map(s => s.trim())
    .filter(Boolean)
    .length
}

/**
 * Detect explicit transaction control. We avoid wrapping if the user is
 * managing BEGIN/COMMIT/ROLLBACK themselves — double-wrapping would create
 * nested transactions which Snowflake rejects.
 */
function hasExplicitTransaction(sql: string): boolean {
  const stripped = stripStringsAndComments(sql)
  return /\b(BEGIN|START\s+TRANSACTION|COMMIT|ROLLBACK)\b/i.test(stripped)
}

/**
 * Crude SQL pre-processor — removes block comments, line comments, and
 * string/identifier literals. We only need it to neutralize semicolons and
 * keywords inside strings so the statement-counter and transaction-detector
 * don't get fooled. Not a real parser; doesn't need to be.
 */
function stripStringsAndComments(sql: string): string {
  let out = sql.replace(/\/\*[\s\S]*?\*\//g, '')         // /* block */
  out = out.replace(/--[^\n]*/g, '')                      // -- line
  out = out.replace(/'(?:''|[^'])*'/g, "''")             // 'string'
  out = out.replace(/"(?:""|[^"])*"/g, '""')             // "identifier"
  return out
}

// //////////////////////////////////////////////////////////////////
// Main Execution Function
// //////////////////////////////////////////////////////////////////
/// `transaction` controls auto-wrapping of multi-statement queries:
///   - 'auto' (default): wrap only when there are 2+ statements and the
///     SQL doesn't already manage its own transaction.
///   - true: force-wrap (rare; useful for tests or pre-expansion SQL).
///   - false: never wrap; use existing semantics (last statement wins,
///     no rollback safety).
export default async function ({ query, queryId, returnType = 'json', async, polling, executor = 'user', customConnection, transaction = 'auto', bindings }: SnowflakeOptions & { transaction?: boolean | 'auto'; bindings?: SnowflakeBindings }, fetchOptions?: UseFetchOptions<any>) {
  if (!query && !queryId) throw new Error('Missing Query or Query ID')

  if (!async && !polling) {
    // //////////////////////////////////////////////////////////////////
    // Regular $fetch Execution
    // //////////////////////////////////////////////////////////////////
    return await snowflakeQueryExecution({ query, queryId, returnType, async, polling, executor, customConnection, transaction, bindings }, fetchOptions)
  } else {
    return { status: 'ERROR', error: 'Async and Polling Not Yet Implemented' } as SnowflakeResponse
  }

  // if (async) {
  //   // //////////////////////////////////////////////////////////////////
  //   // Async "useFetch" Snowflake Execution
  //   // //////////////////////////////////////////////////////////////////
  //   console.log('Using useFetch')
  //   // return useFetch(url, {
  //   //   ...options,
  //   //   onResponseError({ response }) {
  //   //     responseErrorParser(response)
  //   //   },
  //   //   transform: (data) => {
  //   //     return data
  //   //   },
  //   // })
  // }
}

// //////////////////////////////////////////////////////////////////
// Create .fetch Parameters
// //////////////////////////////////////////////////////////////////
function snowflakeFetch({ query, queryId, executor, bindings }: SnowflakeOptions & { bindings?: SnowflakeBindings }, snowflakeAuthentication: SnowflakeAuthentication, fetchOptions?: UseFetchOptions<any>) {
  // Get Snowflake Namespace + the user's chosen active role +
  // their stored default warehouse.
  //
  // `activeSnowflakeRole` is the user's persisted role override
  // (Pinia computed → `users/{uid}.snowflakeConnections.<connId>.activeRole`).
  // `userProfile.activeSnowflakeConnection.defaultWarehouse` is the
  // warehouse they typed at signup for their personal session.
  // When set, we attach both to every statement so Snowflake runs the
  // query with that context rather than the user's DEFAULT_ROLE /
  // DEFAULT_WAREHOUSE. Empty string → omit the field entirely so
  // Snowflake's session defaults apply.
  const { accountNamespace, activeSnowflakeRole, userProfile, accountProfile } = storeToRefs(useGlobalsStore())
  const defaultWarehouse = (userProfile.value?.activeSnowflakeConnection?.defaultWarehouse || '').trim()

  // 💬 SYSTEM executor context. When running as the account-level
  // service user (executor='system' → keypair JWT minted for
  // `snowflakeSettings.systemAccount.username`), the logged-in user's
  // role override + default warehouse must NOT ride along — the system
  // user generally doesn't hold the human's role, so Snowflake would
  // reject with "Role 'X' is not granted to this user." We attach the
  // system account's own role when the setup wizard recorded one
  // (otherwise omit → the system user's DEFAULT_ROLE applies) and
  // always omit the warehouse (its DEFAULT_WAREHOUSE applies).
  const isSystemExecutor = executor === 'system'
  const systemRole = ((accountProfile.value as any)?.snowflakeSettings?.systemAccount?.role || '').trim()

  // Snowflake Authentication. `tokenType` is set by snowflakeAuth
  // based on the user's auth mode — `'KEYPAIR_JWT'` for keypair
  // users, `'OAUTH'` (or undefined) for everyone else (OAuth + PAT
  // both ride the default Bearer with no special header).
  const { sfId, accessToken, tokenType } = snowflakeAuthentication as any

  // Params
  const params = {
    requestId: uuidv4(),
    async: false,
    nullable: true,
    includePresignedUrl: true,
    default_db: null as string | null,
    default_schema: null as string | null,
  }

  // Add Snowflake Namespace if Available
  if (accountNamespace.value) {
    params.default_db = accountNamespace.value.split('.')[0] || null
    params.default_schema = accountNamespace.value.split('.')[1] || null
  }

  return $fetch.create({
    // Hyphenate any underscores in the host segment so the TLS cert
    // wildcard match succeeds — Snowflake account names with `_` are
    // legal but the public DNS / cert form uses `-`.
    baseURL: `https://${snowflakeHostFromSfId(sfId)}.snowflakecomputing.com`,
    onRequest({ options }) {
      // args: request, error
      options.method = queryId ? 'GET' : 'POST'
      options.headers.set('User-Agent', 'PulsarAI')
      options.headers.set('Content-Type', 'application/json')
      options.headers.set('Accept', 'application/json')

      options.headers.set('Authorization', `Bearer ${accessToken}`)
      // 💬 KEYPAIR_JWT users need this header so Snowflake knows to
      // verify the Bearer as a JWT signed by their registered public
      // key (rather than treating it as an OAuth token). OAuth + PAT
      // both work without the header — Snowflake auto-detects the
      // token shape from the bearer itself.
      if (tokenType === 'KEYPAIR_JWT') {
        options.headers.set('X-Snowflake-Authorization-Token-Type', 'KEYPAIR_JWT')
      }
      options.params = params
      if (query) {
        // 💬 Per-request role + warehouse selection.
        //
        // The Snowflake SQL API at /api/v2/statements takes role and
        // warehouse as top-level body fields on the StatementRequest
        // object — NOT headers. The `X-Snowflake-Role` /
        // `X-Snowflake-Warehouse` headers documented at
        // docs.snowflake.com/.../snowflake-rest-api/setting-context
        // belong to the *other* Snowflake REST API surface (object
        // management endpoints like /api/v2/databases). They're
        // explicitly absent from the SQL API endpoint's CORS
        // `Access-Control-Allow-Headers`, so attempting to set them
        // triggers a preflight rejection in the browser. Don't add
        // them back.
        //
        // Body fields work across all three browser auth modes
        // (OAuth + IMPLICIT secondary roles, PAT, KEYPAIR_JWT) as
        // long as the bearer's accessible role set includes the
        // requested role. If it doesn't, Snowflake returns "Role 'X'
        // is not granted to this user" which the error humanizer
        // surfaces. Empty values mean "use whatever the session
        // default is" — we just omit the field.
        const body: Record<string, any> = {
          statement: query,
          parameters: { MULTI_STATEMENT_COUNT: 0 },
        }
        // 💬 Bind variables — values ride the request body and are
        // NOT recorded in query history text. Only attached when the
        // caller supplied them (initial POST only; result fetches are
        // GETs with no body). No effect on any existing caller.
        if (bindings && Object.keys(bindings).length > 0) {
          body.bindings = bindings
        }
        if (isSystemExecutor) {
          // System-executor context — see the block near the top of
          // snowflakeFetch. User role/warehouse deliberately omitted.
          if (systemRole) body.role = systemRole
        }
        else {
          if (activeSnowflakeRole.value) body.role = activeSnowflakeRole.value
          if (defaultWarehouse) body.warehouse = defaultWarehouse
        }
        options.body = body
      }
    },
    onResponseError({ response }) {
      const error = response._data.message

      console.error(error)
      throw new Error(error)
    },
  })
}
